import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { OWNER_EMAIL_DOMAIN } from "@clarity/shared";
import { useAuth } from "../lib/auth";
import { ApiError } from "../lib/api";
import { ButtonSpinner } from "../components/Loading";

export function SignupPage() {
  const { signup, state } = useAuth();
  const navigate = useNavigate();

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (state.status === "authed") {
    navigate("/dashboard", { replace: true });
    return null;
  }

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);

    // Friendly client-side check so the user sees a real message rather
    // than a generic 400 from the worker's Zod refinement.
    if (!email.trim().toLowerCase().endsWith(`@${OWNER_EMAIL_DOMAIN}`)) {
      setError(
        `Owner accounts are for Avista employees (@${OWNER_EMAIL_DOMAIN}). If you're a collaborator, sign in via the link Avista sent you.`,
      );
      return;
    }

    setBusy(true);
    try {
      await signup({ name, email, password });
      navigate("/dashboard", { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setError("That email is already registered.");
      } else if (err instanceof ApiError && err.status === 400) {
        setError(
          `Owner accounts require an @${OWNER_EMAIL_DOMAIN} email and a password with 8+ characters.`,
        );
      } else {
        setError("Something went wrong. Try again in a moment.");
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="mx-auto max-w-md px-6 py-16">
      <h1 className="text-2xl font-semibold">Create your owner account</h1>
      <p className="mt-2 text-sm text-neutral-600">
        Owner accounts are for Avista employees (<code>@{OWNER_EMAIL_DOMAIN}</code>).
        If you're a collaborator reviewing a project,{" "}
        <Link to="/c/login" className="text-neutral-900 underline">
          sign in here
        </Link>{" "}
        instead.
      </p>
      <p className="mt-2 text-sm text-neutral-600">
        Already have one?{" "}
        <Link to="/login" className="text-neutral-900 underline">
          Log in
        </Link>
        .
      </p>
      <form onSubmit={onSubmit} className="mt-8 space-y-4">
        <input
          type="text"
          autoComplete="name"
          required
          maxLength={80}
          placeholder="Your name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="w-full rounded-md border border-neutral-300 px-3 py-2 focus:border-neutral-900 focus:outline-none"
        />
        <input
          type="email"
          autoComplete="email"
          required
          placeholder="you@avista.is"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="w-full rounded-md border border-neutral-300 px-3 py-2 focus:border-neutral-900 focus:outline-none"
        />
        <input
          type="password"
          autoComplete="new-password"
          required
          minLength={8}
          placeholder="Password (8+ characters)"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="w-full rounded-md border border-neutral-300 px-3 py-2 focus:border-neutral-900 focus:outline-none"
        />
        {error ? <p className="text-sm text-red-600">{error}</p> : null}
        <button
          type="submit"
          disabled={busy}
          className="inline-flex w-full items-center justify-center rounded-md bg-neutral-900 px-4 py-2 text-white hover:bg-neutral-700 disabled:opacity-50"
        >
          {busy ? <ButtonSpinner label="Creating account" /> : "Create account"}
        </button>
      </form>
    </section>
  );
}
